import { usePreferences } from '../../lib/PreferencesContext'
import type { ThemeChoice } from '../../lib/preferences'

/**
 * The palettes, in the order somebody reads them rather than the order they were written.
 *
 * "Follow the system" is first because it is what an account starts on, and its description has to
 * say which two palettes it resolves to - otherwise the default is the one choice on this screen
 * whose effect is invisible until you change your machine (D-307).
 */
const THEMES: { value: ThemeChoice; name: string; description: string }[] = [
  {
    value: 'system',
    name: 'Follow the system',
    description: 'Whatever this machine is set to: Coastline in a bright room, Nocturne in a dark one.'
  },
  {
    value: 'coastline',
    name: 'Coastline',
    description: "The product's own. Navy on a cool white ground, the ocean blue of the logo, soft depth."
  },
  {
    value: 'broadsheet',
    name: 'Broadsheet',
    description: 'Newsprint set for the web. Serif on a paper ground, process cyan, rules instead of boxes.'
  },
  {
    value: 'light',
    name: 'Modernist',
    description: 'The neutral light palette this console shipped with. Hairline rules, one strong blue.'
  },
  {
    value: 'dark',
    name: 'Nocturne',
    description: 'Blue-black ground, low glare, accents that glow. Built for a dark room.'
  }
]

/**
 * What this console looks like to one person.
 *
 * Per account rather than per organization: two administrators sharing a tenant do not have to agree
 * about a palette, and one of them works in a dark room.
 *
 * No heading of its own. The tab strip above already names this panel, and a card repeating the word
 * under it is the two-headings-that-can-disagree shape D-429 removed from the Devices screen.
 */
export function AppearanceCard() {
  const { theme, setTheme } = usePreferences()

  return (
    <div className="card">
      <div className="card-body">
        <p className="muted" style={{ marginTop: 0 }}>
          Applied straight away and remembered against your account, so it follows you to another
          machine.
        </p>

        <div className="theme-choices" role="radiogroup" aria-label="Theme">
          {THEMES.map((option) => (
            <label
              key={option.value}
              className={`theme-choice${theme === option.value ? ' selected' : ''}`}
              data-testid={`theme-${option.value}`}
            >
              <input
                type="radio"
                name="theme"
                value={option.value}
                checked={theme === option.value}
                onChange={() => setTheme(option.value)}
              />
              <span className="theme-name">{option.name}</span>
              <span className="hint">{option.description}</span>
              <span className={`theme-swatch theme-swatch-${option.value}`} aria-hidden="true" />
            </label>
          ))}
        </div>
      </div>
    </div>
  )
}
